import { useEffect, useCallback, useState, useMemo } from 'react';
import remove from 'lodash/remove';
import cloneDeep from 'lodash/cloneDeep';
import flatten from 'lodash/flatten';
import slice from 'lodash/slice';
import filter from 'lodash/filter';

import { Headers } from './headers';
import { Board } from './board';
import { QuizQuestions } from 'graphql/queries/types/QuizQuestions';
import { BattleshipEndScreen } from './battleship-end';
import { generateGridData, checkIfShipDestroyed } from './grid-generator';
import { useQuizSessionContext } from 'contexts';
import { shuffleArray } from 'utils';

import { BattleshipGenerator } from './battleship-generator';
import { BattleshipUserSessionAdditionalInfo, Team, CardData, GridData, ShipLocation, BattleshipGameTypes } from './types';
import { AnsweredQuestion, Question } from 'types';
import { useSnackbar } from 'components';

export interface TeamShipHealth extends Team {
    shipCount: number;
    destroyed: number;
}

enum Scenes {
    Quiz,
    EndScreen
}
interface Props {
    quizQuestions: QuizQuestions;
    gridColumns: number;
    gridRows: number;
    teams: Team[];
    eventLogo?: string | null;
    gameType?: BattleshipGameTypes;
    cooldownSeconds?: number | null;
}
/**
 * TODO - would be nice - rehidrate board status from user session
 */
export const BattleshipQuiz = (props: Props) => {
    const gameType = props.gameType ?? BattleshipGameTypes.LIVE_MULTI_TEAM;
    const sessionContext = useQuizSessionContext();
    const snackbar = useSnackbar();
    const [currentScene, setCurrentScene] = useState(Scenes.Quiz);
    const [correctAnswers, setCorrectAnswers] = useState(0);
    const [incorrectAnswers, setIncorrectAnswers] = useState(0);
    // TODO - save state to localStorage for a fast regeneration and to avoid losing progress mid quiz
    const [gridData, setGridData] = useState<GridData[]>([]);
    const [localQuestions, setLocalQuestions] = useState<Question[]>(cloneDeep(props.quizQuestions.quiz_master_quizzes_by_pk?.questions!));
    const [failedQuestions, setFailedQuestions] = useState<AnsweredQuestion[]>([]);
    const [answeredQuestions, setAnsweredQuestions] = useState<AnsweredQuestion[]>([]);
    const [teamShips, setTeamShips] = useState<TeamShipHealth[]>(() => props.teams.map((t) => ({
        ...t,
        shipCount: t.shipLocations.length,
        destroyed: 0
    })));

    useEffect(() => {
        if (sessionContext?.userSession?.hasEnded) {
            setCurrentScene(Scenes.EndScreen);
        }
    }, [sessionContext?.userSession?.hasEnded]);

    const defineGrid = useCallback(() => {
        const gridDataWithTeamNames = generateGridData(props.gridColumns, props.gridRows);

        props.teams.forEach(team => team.shipLocations.forEach((ship) => {
            BattleshipGenerator.getShipCoordinates(ship).forEach((c, index) => {
                const cardData = gridDataWithTeamNames[c.x].cardRows[c.y];
                cardData.shipTeam = team.name;
                cardData.ship = ship;
                cardData.shipPartIndex = index;
            });
        }));
        return gridDataWithTeamNames;
    }, [props.gridColumns, props.gridRows, props.teams]);

    const defineQuestions = useCallback(
        (cardData: GridData[], remainingQuestions: Question[], failedQuestions: Question[]): GridData[] => {
            const cards: GridData[] = [];
            let lRemainingQuestions = [...remainingQuestions];
            shuffleArray(lRemainingQuestions);

            const answeredCount = filter(flatten(cardData.map((c) => c.cardRows)), 'answered').length;
            const neededQuestions = props.gridRows * props.gridColumns - answeredCount;
            let questions: Question[] = [];

            if (neededQuestions <= lRemainingQuestions.length) {
                questions = slice(lRemainingQuestions, 0, neededQuestions);
            } else {
                // reuse failed questions when we don't have enough new ones
                // only add the missing number of questions.
                const reuseFailedCount = neededQuestions - lRemainingQuestions.length;
                const lFailedQuestions = [...failedQuestions];
                shuffleArray(lFailedQuestions);
                questions = [...lRemainingQuestions, ...slice(lFailedQuestions, 0, reuseFailedCount)];
            }

            shuffleArray(questions);

            let questionIndex = 0;
            cardData.forEach((column, cIndex) => {
                const cCardColumns: CardData[] = [];
                column.cardRows.forEach((card) => {
                    if (card.answered) {
                        cCardColumns.push({ ...card });
                    } else {
                        cCardColumns.push({ ...card, question: cloneDeep(questions[questionIndex]) });
                        questionIndex++;
                    }
                });
                cards[cIndex] = { cardRows: cCardColumns };
            });

            return cards;
        },
        [props.gridColumns, props.gridRows]
    );

    // use this effect only for initial definition of the grid data
    useEffect(() => {
        setGridData(defineQuestions(defineGrid(), localQuestions, []));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defineGrid, defineQuestions]);

    const getShipSunkMessage = (ship: ShipLocation): string => {
        return `${BattleshipGenerator.getShipName(ship.size)} destroyed`;
    };

    const handleCorrectAnswer = (card: CardData, userAnswer?: string) => {
        setCorrectAnswers(correctAnswers + 1);

        const newData = [...gridData];
        newData[card.coordinates.x].cardRows[card.coordinates.y].answered = true;
        const cLocalQuestions = [...localQuestions];
        const answeredQuestion = newData[card.coordinates.x].cardRows[card.coordinates.y];

        // remove the answered question from the question pool
        remove(cLocalQuestions, (q: Question) => q.id === answeredQuestion.question!.id);

        // if we used a failed question to fill in the needed number of questions remove it from the failed pool
        const cFailedQuestions = [...failedQuestions];
        remove(cFailedQuestions, (q: Question) => q.id === answeredQuestion.question!.id);

        // save answer to the answeredQuestions state
        const cAnsweredQuestions = [...answeredQuestions];
        cAnsweredQuestions.push({
            ...cloneDeep(answeredQuestion.question!),
            userAnswer,
            answeredCorrectly: true
        });

        // define new questions for cards
        const cCardColumns = defineQuestions(newData, cLocalQuestions, failedQuestions);
        setGridData(cCardColumns);

        setLocalQuestions(cLocalQuestions);
        setFailedQuestions(cFailedQuestions);
        setAnsweredQuestions(cAnsweredQuestions);

        // Check for quiz end
        let quizEnded = false;
        const isShipDestroyed = checkIfShipDestroyed(gridData, card);
        if (card.shipTeam && isShipDestroyed) {
            const currentTeamShipStatus = { ...teamShips.find((t) => t.name === card.shipTeam)! };
            currentTeamShipStatus.destroyed = currentTeamShipStatus.destroyed + 1;
            const newTeamShipStatus = [...teamShips];
            remove(newTeamShipStatus, (t) => t.name === card.shipTeam);
            newTeamShipStatus.push(currentTeamShipStatus);
            setTeamShips(newTeamShipStatus);

            snackbar.success(getShipSunkMessage(card.ship!));

            // Check for quiz end
            const inPlayTeams = newTeamShipStatus.filter((t) => t.shipCount !== t.destroyed);
            if (gameType !== BattleshipGameTypes.LIVE_MULTI_TEAM) {
                if (inPlayTeams.length === 0) {
                    quizEnded = true;
                }
            } else if (inPlayTeams.length === 1) {
                quizEnded = true;
            }
        }

        // update user session
        if (sessionContext) {
            const additionalInfo: BattleshipUserSessionAdditionalInfo = sessionContext.userSession.additionalInfo ?? {};
            const answeredCards = additionalInfo?.answeredCards ? [...additionalInfo.answeredCards] : [];
            answeredCards.push({
                locationCode: card.locationCode,
                correct: true
            });
            additionalInfo.answeredCards = answeredCards;

            if (card.shipTeam && isShipDestroyed) {
                const shipStatus = additionalInfo.shipStatus ? [...additionalInfo.shipStatus] : [];
                const removedTeams = remove(shipStatus, (ss) => ss.teamName === card.shipTeam);
                const team = removedTeams.length > 0 ? removedTeams[0] : { sunkShips: 0, teamName: card.shipTeam };
                team.sunkShips = team.sunkShips + 1;
                additionalInfo.shipStatus = [...shipStatus, team];
            }

            sessionContext.updateUserSession({
                answersAttempted: sessionContext.userSession.answersAttempted + 1,
                correctAnswers: sessionContext.userSession.correctAnswers + 1,
                id: sessionContext.userSession.id,
                currentTime: new Date().toISOString(),
                hasEnded: quizEnded,
                additionalInfo
            });
        }

        if (quizEnded) {
            setCurrentScene(Scenes.EndScreen);
        }
    };

    const handleIncorrectAnswer = (card: CardData, userAnswer?: string) => {
        setIncorrectAnswers(incorrectAnswers + 1);

        const failedQuestion = gridData[card.coordinates.x].cardRows[card.coordinates.y];
        const cLocalQuestions = [...localQuestions];
        const cFailedQuestions = [...failedQuestions];

        const existsInFailed = cFailedQuestions.filter((fq) => fq.id === failedQuestion.question!.id).length > 0;
        if (existsInFailed) {
            // remove the failed question from the failed queston state in order to readd it
            remove(cFailedQuestions, (q: Question) => q.id === failedQuestion.question!.id);
        }

        // remove the failed question from the question pool
        remove(cLocalQuestions, (q: Question) => q.id === failedQuestion.question!.id);

        // add failed question to the failed questions pool
        const failedQuestionAnswered: AnsweredQuestion = {
            ...cloneDeep(failedQuestion.question!),
            userAnswer,
            answeredCorrectly: false
        };
        cFailedQuestions.push(failedQuestionAnswered);

        // define new questions for cards
        const cCardColumns = defineQuestions(gridData, cLocalQuestions, cFailedQuestions);

        setGridData(cCardColumns);
        setLocalQuestions(cLocalQuestions);
        setFailedQuestions(cFailedQuestions);

        if (sessionContext) {
            sessionContext.updateUserSession({
                answersAttempted: sessionContext.userSession.answersAttempted + 1,
                correctAnswers: sessionContext.userSession.correctAnswers,
                id: sessionContext.userSession.id,
                currentTime: new Date().toISOString(),
                additionalInfo: sessionContext.userSession.additionalInfo
            });
        }
    };

    const onExitQuiz = () => {
        if (sessionContext) {
            sessionContext.updateUserSession({
                answersAttempted: sessionContext.userSession.answersAttempted + 1,
                correctAnswers: sessionContext.userSession.correctAnswers + 1,
                id: sessionContext.userSession.id,
                currentTime: new Date().toISOString(),
                hasEnded: true,
                additionalInfo: sessionContext.userSession.additionalInfo
            });
        }
        setCurrentScene(Scenes.EndScreen);
    };

    const getMaxQuestionDuration = useMemo(
        () =>
            !!sessionContext?.session.questionTimeSec && sessionContext?.session.questionTimeSec > 0
                ? sessionContext?.session.questionTimeSec
                : undefined,
        [sessionContext?.session.questionTimeSec]
    );

    const getCooldownSeconds = useMemo(
        () => (!!props.cooldownSeconds && props.cooldownSeconds > 0 ? props.cooldownSeconds : undefined),
        [props.cooldownSeconds]
    );

    const scenes = {
        [Scenes.Quiz]: () => (
            <div className="battleship-quiz">
                <Headers
                    title={props.quizQuestions.quiz_master_quizzes_by_pk!.name}
                    eventLogo={props.eventLogo}
                    correctAnswers={correctAnswers}
                    incorrectAnswers={incorrectAnswers}
                    teamShipStatus={teamShips}
                    quizTimelimitSeconds={sessionContext?.session.maxDurationSec}
                    onQuizExit={onExitQuiz}
                />
                <Board
                    eventLogo={props.eventLogo}
                    gridData={gridData}
                    rows={props.gridRows}
                    cols={props.gridColumns}
                    gameType={gameType}
                    maxQuestionDuration={getMaxQuestionDuration}
                    incorrectAnswerCooldownSeconds={getCooldownSeconds}
                    onCorrectAnswer={handleCorrectAnswer}
                    onIncorrectAnswer={handleIncorrectAnswer}
                />
            </div>
        ),
        [Scenes.EndScreen]: () => (
            <BattleshipEndScreen
                correctAnswers={correctAnswers}
                incorrectAnswers={incorrectAnswers}
                teamShipStatus={teamShips}
                gameType={gameType}
                answeredQuestions={[...answeredQuestions, ...failedQuestions]}
            />
        )
    };

    return scenes[currentScene]();
};
